// src/Pages/AboutPage/AboutPage.js
import React, { useMemo } from "react";
import "../LandingPage/LandingPage.css"; // reuse global layout/typography/buttons/footer
import "../About/About.css";             // page-specific styles (below)
import logo from "../../assets/logo.webp";
import { NavLink } from "react-router-dom";
import SubscribeForm from "../../Components/SubscribeForm/SubscribeForm";
import SEO from "../../Components/SEO/SEO";
import { useContent, useSettings, assetUrl } from "../../utils/useContent";

const FALLBACK_SETTINGS = {
  "about.hero.kicker": "All Star Xperience",
  "about.hero.title": "About ASX",
  "about.hero.subtitle":
    "Community-first football in Montréal — competitive leagues, professional experience, and a place to belong.",
  "about.hero.pills": "Cash Prize, Pro Photos, Community",
  "about.founders.heading": "Founders",
  "about.story.heading": "What is ASX?",
  "about.story.body":
    "All Star Xperience is more than a soccer league — it's a movement.\n\nFounded by two players with a passion for the game and the community around it, ASX creates competitive, high-energy leagues and tournaments that bring together talent from every corner of the city.\n\nWe combine the thrill of cash prizes with the power of recognition — every player gets their moment. But above all, ASX is about connection: a space where athletes compete hard, build friendships, and belong to a growing soccer family.\n\nWhether you're chasing trophies or just love the game, ASX is where passion meets purpose. Welcome to the Xperience.",
};

const FALLBACK_FOUNDERS = [
  { name: "Talal Selman", role: "Co-Founder" },
  { name: "Jad Harb", role: "Co-Founder" },
];

function getInitials(str = "") {
  const words = str.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "??";
  const first = words[0][0] || "";
  const second = words.length > 1 ? words[1][0] : words[0][1] || "";
  return (first + second).toUpperCase();
}
function pickColorClass(str = "") {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 33 + str.charCodeAt(i)) % 9973;
  const n = (h % 6) + 1;
  return `color-${n}`;
}

// Split a title so the last word is rendered as an accent span (e.g. "About ASX").
function splitTitle(title) {
  const trimmed = String(title || "").trim();
  if (!trimmed) return { head: "", accent: "" };
  const idx = trimmed.lastIndexOf(" ");
  if (idx === -1) return { head: "", accent: trimmed };
  return { head: trimmed.slice(0, idx), accent: trimmed.slice(idx + 1) };
}

/* Same footer structure/styles as Landing (kept local for simplicity) */
function Footer() {
  const year = new Date().getFullYear();
  const links = [
    { label: "Home", href: "/" },
    { label: "Leagues", href: "/leagues" },
    { label: "Hall of Fame", href: "/hall-of-fame" },
    { label: "Rules", href: "/rules" },
    { label: "About", href: "/about" },
    { label: "Register", href: "/register" },
  ];
  return (
    <footer className="site-footer" role="contentinfo">
      <div className="footer-top container">
        <div className="ft-col ft-brand">
          <div className="ft-logo">
            <img
              src={logo}
              alt="All Star Xperience"
              className="ft-logo-img"
              width="220"
              height="200"
            />
          </div>

          <div className="ft-social">
            <a
              className="ft-social-btn"
              href="https://www.instagram.com/allstarxperience/#"
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
              title="Instagram"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path fill="currentColor" d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5Zm0 2a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7Zm5 3.5A5.5 5.5 0 1 1 6.5 13 5.5 5.5 0 0 1 12 7.5Zm0 2A3.5 3.5 0 1 0 15.5 13 3.5 3.5 0 0 0 12 9.5Zm6-2.25a1.25 1.25 0 1 1-1.25 1.25A1.25 1.25 0 0 1 18 7.25Z"/>
              </svg>
              <span>Instagram</span>
            </a>

            <a
              className="ft-social-btn"
              href="https://www.facebook.com/allstarxperience"
              target="_blank"
              rel="noreferrer"
              aria-label="Facebook"
              title="Facebook"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path fill="currentColor" d="M13.5 9H16l.5-3h-3V4.5c0-.8.2-1.5 1.5-1.5H16V0h-2c-2.5 0-4 1.5-4 4.2V6H8v3h2v9h3.5V9Z"/>
              </svg>
              <span>Facebook</span>
            </a>
          </div>
        </div>

        <div className="ft-col" id="contact">
          <h4 className="ft-title">Contact</h4>
          <ul className="ft-contact">
            <li>
              <span className="ft-k">Email</span>
              <a href="mailto:allstarxperience@gmail.com">allstarxperience@gmail.com</a>
            </li>
            <li>
              <span className="ft-k">Phone</span>
              <a href="tel:+14387283726">+1 (438) 728-3726</a>
            </li>
            <li>
              <span className="ft-k">Phone</span>
              <a href="tel:+14385089880">+1 (438) 508-9880</a>
            </li>
            <li>
              <span className="ft-k">City</span>
              <span>Montreal, QC</span>
            </li>
          </ul>
        </div>

        <div className="ft-col explore-col">
          <h4 className="ft-title">Explore</h4>
          <ul className="ft-links">
            {links.map((l) => (
              <li key={l.label}><a href={l.href}>{l.label}</a></li>
            ))}
          </ul>
        </div>

        <div className="ft-col">
          <h4 className="ft-title">Stay in the Loop</h4>
          <p className="ft-note">News, fixtures, and registration reminders.</p>
          <SubscribeForm />
        </div>
      </div>

      <div className="footer-bottom">
        <div className="container footer-bottom-inner">
          <span>© {year} All Star Xperience. All rights reserved. Developed by Riachiッ.</span>
          <div className="ft-legal-links">
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
            <a href="#contact">Contact</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function AboutPage() {
  const settings = useSettings("about.", FALLBACK_SETTINGS);
  const { items: founders } = useContent("founders", FALLBACK_FOUNDERS);

  const { head, accent } = useMemo(
    () => splitTitle(settings["about.hero.title"]),
    [settings]
  );

  const pills = useMemo(
    () =>
      String(settings["about.hero.pills"] || "")
        .split(",")
        .map((p) => p.trim())
        .filter(Boolean),
    [settings]
  );

  const paragraphs = useMemo(
    () =>
      String(settings["about.story.body"] || "")
        .split(/\n{2,}/)
        .map((p) => p.trim())
        .filter(Boolean),
    [settings]
  );

  return (
    <>
      <SEO
        title="About ASX | Community-First 7v7 Soccer in Montreal"
        description="All Star Xperience is more than a league — it's a movement. Founded in Montreal to bring together competitive 7v7 soccer with recognition, cash prizes, and community."
        keywords="About ASX, All Star Xperience founders, Montreal soccer community, 7v7 soccer Montreal"
      />
      <main className="about-root">
        {/* Hero (reuses h1, kicker, btn styles from Landing) */}
        <section className="section about-hero-wrap">
          <div className="container">
            <header className="about-hero card">
              <div className="about-hero-bg" aria-hidden />
              <div className="kicker">
                <span className="kicker-dot" />
                {settings["about.hero.kicker"]}
              </div>
              <h1 className="h1 about-title">
                {head ? <>{head} <span className="about-accent">{accent}</span></> : accent}
              </h1>
              <p className="p about-sub">{settings["about.hero.subtitle"]}</p>
              <div className="hero-cta">
                <NavLink to="/register" className="btn btn-primary">Register Now</NavLink>
                <a className="btn btn-ghost" href="/leagues">See Schedule</a>
              </div>
              {pills.length > 0 && (
                // eslint-disable-next-line
                <ul className="about-pills" role="list">
                  {pills.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              )}
            </header>
          </div>
        </section>

        {/* Founders (cards match match-card visual weight) */}
        <section className="section section-tight">
          <div className="container">
            <h2 className="h2 about-h2">{settings["about.founders.heading"]}</h2>
            <div className="founders-grid">
              {founders.map((f) => {
                const initials = getInitials(f.name);
                const colorClass = pickColorClass(f.name);
                const photo = f.image_path ? assetUrl(f.image_path) : "";
                return (
                  <article className="founder-card card" key={f.id ?? f.name}>
                    <div className="founder-stripe" aria-hidden />
                    {photo ? (
                      <div className="founder-avatar ring" title={f.name}>
                        <img src={photo} alt={f.name} className="founder-avatar-img" />
                      </div>
                    ) : (
                      <div className={`founder-avatar ring ${colorClass}`} title={f.name}>
                        {initials}
                      </div>
                    )}
                    <div className="founder-meta">
                      <div className="founder-name">{f.name}</div>
                      {f.role && <div className="founder-role">{f.role}</div>}
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Story (copy + tone consistent with site) */}
        <section className="section">
          <div className="container">
            <article className="about-story card">
              <h2 className="h2 about-h2">{settings["about.story.heading"]}</h2>
              {paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </article>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
